import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@context/auth";
import { ListItemAction } from "@components/ListItemCardCst/types";
import {
  CARD_ACTION_ICON_MAP,
  CardActionConfig,
  cardActionsRegistry,
  evaluateDisableIf,
  evaluateHideIf,
} from "@utils/cardActions";
import { UseCardActionsOptions, UseCardActionsResult } from "./types";

export type { UseCardActionsOptions, UseCardActionsResult } from "./types";

const userHasPermission = (
  permission: string | undefined,
  userPermissions: string[] | undefined,
): boolean => {
  if (!permission) return true;
  if (!userPermissions || userPermissions.length === 0) return false;
  if (permission.includes("&&")) {
    return permission.split("&&").every((p) => userPermissions.includes(p));
  }
  return userPermissions.includes(permission);
};

export const useCardActions = (
  entityType: string,
  entity: Record<string, any> | undefined,
  options: UseCardActionsOptions = {},
): UseCardActionsResult => {
  const { t } = useTranslation("list-item-card");
  const { user } = useAuth();

  return useMemo(() => {
    if (!entity) return [];
    const allConfigs = cardActionsRegistry[entityType];
    if (!allConfigs || allConfigs.length === 0) return [];

    const configs = allConfigs.filter(
      (config: CardActionConfig) =>
        !evaluateHideIf(config.hideIf, entity, {
          corporateId: options.corporateId,
          totpEnabled: options.totpEnabled,
          user,
        }),
    );
    if (configs.length === 0) return [];

    const noop = () => {
      /* TODO: modificare con funzione reale tramite overrides */
    };

    const built: ListItemAction[] = configs.map((config: CardActionConfig) => {
      const hasBypassRole = !!config.bypassRoles?.some((role) =>
        user?.roles?.includes(role),
      );
      const lacksPermission =
        !hasBypassRole &&
        !userHasPermission(
          typeof config.permission === "function"
            ? config.permission(entity)
            : config.permission,
          user?.permissions,
        );
      const disabledByCondition = evaluateDisableIf(config.disableIf, entity, {
        corporateId: options.corporateId,
        user,
      });
      const readFallback =
        lacksPermission &&
        config.readFallback &&
        userHasPermission(config.readFallback.permission, user?.permissions)
          ? config.readFallback
          : undefined;
      const disabled =
        (lacksPermission && !readFallback) || disabledByCondition;

      const onClick = options.overrides?.[config.key] ?? noop;

      const labelKey =
        readFallback?.labelKey ??
        (typeof config.labelKey === "function"
          ? config.labelKey(entity)
          : config.labelKey);
      const iconKey =
        readFallback?.iconKey ??
        (typeof config.iconKey === "function"
          ? config.iconKey(entity)
          : config.iconKey);

      return {
        label: t(labelKey, config.key),
        icon: CARD_ACTION_ICON_MAP[iconKey],
        onClick,
        disabled,
        ...(options.testIdPrefix
          ? { testId: `${options.testIdPrefix}-${config.key}` }
          : {}),
      };
    });

    const groupsMap = new Map<number, ListItemAction[]>();
    configs.forEach((config, index) => {
      const list = groupsMap.get(config.group) ?? [];
      list.push(built[index]);
      groupsMap.set(config.group, list);
    });

    return Array.from(groupsMap.entries())
      .sort(([a], [b]) => a - b)
      .map(([, list]) => list);
  }, [entityType, entity, options, t, user]);
};
